import React, { useCallback, useEffect, useState } from "react";
import orendaService from "../../services/orenda.service";
import OrendaSendMoney from "./OrendaSendMoney";

// Payments screen — mirrors the Beneficiaries layout:
//
//   • title + "Send money" primary CTA
//   • list of recent payment requests with status chip
//   • per-row actions (currently: Cancel direct debit when applicable)
//
// We call the backend's listpaymentrequestsorenda with the default status
// filter (PENDING / PENDING_TM / INITIATED / PENDING_CANCELLATION) PLUS
// COMPLETED and FAILED so the user sees both in-flight AND recent history.
// Orenda caps per-request paging at a sane default so we just ask for 25.

const parseListResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let raw = null;
  if (typeof rawStr === "string") {
    try { raw = JSON.parse(rawStr); } catch { raw = null; }
  }
  const typed = response?.payload?.data ?? response?.data ?? null;
  const data =
    (Array.isArray(raw?.data) && raw.data) ||
    (Array.isArray(typed?.data) && typed.data) ||
    (Array.isArray(typed) && typed) ||
    [];
  const total =
    raw?.total ?? typed?.total ?? (Array.isArray(data) ? data.length : 0);
  return { data, total };
};

const STATUS_CHIP_CLASS = {
  COMPLETED: "orenda-pay-chip--ok",
  INITIATED: "orenda-pay-chip--pending",
  PENDING: "orenda-pay-chip--pending",
  PENDING_TM: "orenda-pay-chip--pending",
  PENDING_CANCELLATION: "orenda-pay-chip--warn",
  FAILED: "orenda-pay-chip--err",
};
const chipClass = (status) =>
  STATUS_CHIP_CLASS[String(status || "").toUpperCase()] || "orenda-pay-chip--neutral";

const formatStatus = (status) => {
  if (!status) return "—";
  return String(status)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const formatDateTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch { return ""; }
};

const formatAmount = (amount, currency) => {
  if (amount == null) return "—";
  const n = typeof amount === "number" ? amount : Number(amount);
  if (Number.isNaN(n)) return `${amount} ${currency || ""}`.trim();
  return `${n.toFixed(2)} ${currency || ""}`.trim();
};

// Direct debits can be cancelled only while they're still cancellable AND
// before the cutoff. Orenda returns 409 if we're too late; we rely on their
// 409 message when it happens rather than guessing the local cutoff.
const canCancelDirectDebit = (p) => {
  if (!p?.isDirectDebit) return false;
  const s = String(p.status || "").toUpperCase();
  return s === "INITIATED" || s === "PENDING";
};

// Request every status so recent history shows alongside in-flight. Orenda
// docs: default status filter is PENDING,PENDING_TM,INITIATED,PENDING_CANCELLATION.
const ALL_STATUSES = "PENDING,PENDING_TM,INITIATED,PENDING_CANCELLATION,COMPLETED,FAILED";

// Orenda returns scheduled payments inside { data: { scheduledPayments: [] }}.
// Pick-through handles the usual nesting variants + the stringified errorMessage.
const parseScheduledListResponse = (response) => {
  const rawStr = response?.payload?.errorMessage ?? response?.errorMessage;
  let raw = null;
  if (typeof rawStr === "string") {
    try { raw = JSON.parse(rawStr); } catch { raw = null; }
  }
  const typed = response?.payload?.data ?? response?.data ?? null;
  return (
    (Array.isArray(raw?.data?.scheduledPayments) && raw.data.scheduledPayments) ||
    (Array.isArray(typed?.data?.scheduledPayments) && typed.data.scheduledPayments) ||
    (Array.isArray(typed?.scheduledPayments) && typed.scheduledPayments) ||
    []
  );
};

// Scheduled payments carry both userSelectedDate (ISO) and scheduledDate
// (Unix seconds). Prefer the ISO one; fall back to seconds when needed.
const formatScheduledFor = (sp) => {
  if (sp?.userSelectedDate) {
    try {
      const d = new Date(sp.userSelectedDate);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
    } catch { /* fall through */ }
  }
  if (typeof sp?.scheduledDate === "number") {
    try {
      const d = new Date(sp.scheduledDate * 1000);
      if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      }
    } catch { /* fall through */ }
  }
  return "—";
};

const formatScheduledPayeeAccount = (sp) => {
  const p = sp?.payee;
  if (!p) return "";
  if (p.iban) return p.iban;
  if (p.sortCode && p.accountNumber) return `${p.sortCode} · ${p.accountNumber}`;
  return "";
};

const formatFrequency = (f) => {
  if (!f || f === "NEVER") return "One-off";
  return String(f).toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

const OrendaPayments = () => {
  // Two tabs: "recent" (payment requests, in-flight + history) and
  // "scheduled" (upcoming scheduled payments the user has queued).
  const [tab, setTab] = useState("recent");

  const [phase, setPhase] = useState("loading"); // loading | ready | error
  const [listError, setListError] = useState(null);
  const [list, setList] = useState([]);
  // Scheduled list state. Mirrors the recent list but comes from a
  // different endpoint and has its own cancel action.
  const [schedPhase, setSchedPhase] = useState("loading");
  const [schedError, setSchedError] = useState(null);
  const [schedList, setSchedList] = useState([]);
  const [cancelScheduled, setCancelScheduled] = useState(null); // scheduled-payment to delete
  const [cancelScheduledBusy, setCancelScheduledBusy] = useState(false);
  const [cancelScheduledError, setCancelScheduledError] = useState(null);

  const [sendOpen, setSendOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null); // payment-request to cancel
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const loadList = useCallback(async () => {
    setListError(null);
    setPhase("loading");
    try {
      const resp = await orendaService.listPaymentRequestsOrenda({
        status: ALL_STATUSES,
        limit: 25,
        page: 1,
        sortBy: "createdDateTime",
        sortOrder: "desc",
      });
      const { data } = parseListResponse(resp);
      setList(data);
      setPhase("ready");
    } catch (err) {
      setListError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "Couldn't load payments."
      );
      setPhase("error");
    }
  }, []);

  const loadScheduled = useCallback(async () => {
    setSchedError(null);
    setSchedPhase("loading");
    try {
      const resp = await orendaService.listScheduledPaymentsOrenda({});
      setSchedList(parseScheduledListResponse(resp));
      setSchedPhase("ready");
    } catch (err) {
      setSchedError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "Couldn't load scheduled payments."
      );
      setSchedPhase("error");
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { loadScheduled(); }, [loadScheduled]);

  const runDeleteScheduled = async () => {
    if (!cancelScheduled?.id) return;
    setCancelScheduledError(null);
    setCancelScheduledBusy(true);
    try {
      await orendaService.deleteScheduledPaymentOrenda({
        scheduleId: cancelScheduled.id,
      });
      setCancelScheduledBusy(false);
      setCancelScheduled(null);
      await loadScheduled();
    } catch (err) {
      setCancelScheduledBusy(false);
      setCancelScheduledError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "The cancellation couldn't complete."
      );
    }
  };

  const runCancelDirectDebit = async () => {
    if (!cancelTarget?.id) return;
    setCancelError(null);
    setCancelBusy(true);
    try {
      await orendaService.cancelDirectDebitOrenda({
        paymentRequestId: cancelTarget.id,
      });
      setCancelBusy(false);
      setCancelTarget(null);
      await loadList();
    } catch (err) {
      setCancelBusy(false);
      setCancelError(
        err?.response?.data?.errorMessage ||
          err?.message ||
          "The cancellation couldn't complete."
      );
    }
  };

  return (
    <section className="orenda-benes orenda-pay-screen">
      <header className="orenda-benes-head">
        <div>
          <div className="orenda-eyebrow">Payments</div>
          <h1 className="orenda-benes-title">Your payments</h1>
          <p className="orenda-benes-sub">
            Send money to a saved beneficiary and track the status of
            everything in-flight or recently settled.
          </p>
        </div>
        <button
          type="button"
          className="orenda-hero-cta"
          onClick={() => setSendOpen(true)}
        >
          <SendIcon />
          Send money
        </button>
      </header>

      <div className="orenda-benes-segmented orenda-pay-tabs">
        <button
          type="button"
          className={tab === "recent" ? "is-active" : ""}
          onClick={() => setTab("recent")}
        >
          Recent
        </button>
        <button
          type="button"
          className={tab === "scheduled" ? "is-active" : ""}
          onClick={() => setTab("scheduled")}
        >
          Scheduled
          {schedList.length > 0 && (
            <span className="orenda-pay-tab-count">{schedList.length}</span>
          )}
        </button>
      </div>

      {tab === "recent" && phase === "loading" && (
        <div className="orenda-benes-card orenda-benes-empty">Loading…</div>
      )}

      {tab === "recent" && phase === "error" && (
        <div className="orenda-benes-card orenda-benes-empty">
          <p>{listError}</p>
          <button
            type="button"
            className="orenda-btn-primary"
            onClick={loadList}
          >
            Try again
          </button>
        </div>
      )}

      {tab === "recent" && phase === "ready" && list.length === 0 && (
        <div className="orenda-benes-card orenda-benes-empty">
          <h3>No payments yet</h3>
          <p>
            Send money to one of your saved beneficiaries and it'll show up
            here with live status.
          </p>
          <button
            type="button"
            className="orenda-hero-cta"
            onClick={() => setSendOpen(true)}
          >
            <SendIcon />
            Send money
          </button>
        </div>
      )}

      {tab === "recent" && phase === "ready" && list.length > 0 && (
        <ul className="orenda-benes-list orenda-pay-list">
          {list.map((p) => {
            const payeeName =
              p?.payee?.name ||
              (p?.payee?.accountNumber
                ? `Account ${p.payee.accountNumber}`
                : "—");
            return (
              <li key={p.id} className="orenda-benes-row orenda-pay-row">
                <div className="orenda-benes-row-main">
                  <div className="orenda-benes-row-name">
                    {payeeName}
                    {p.isDirectDebit && (
                      <span className="orenda-pay-chip orenda-pay-chip--neutral orenda-pay-chip--dd">
                        Direct debit
                      </span>
                    )}
                  </div>
                  <div className="orenda-benes-row-account orenda-pay-row-sub">
                    <span>{formatDateTime(p.createdDateTime)}</span>
                    {p.paymentRef && (
                      <span className="orenda-pay-ref">Ref {p.paymentRef}</span>
                    )}
                  </div>
                </div>
                <div className="orenda-pay-row-right">
                  <div className="orenda-pay-amount">
                    {formatAmount(p.amount, p.currency)}
                  </div>
                  <div
                    className={`orenda-pay-chip ${chipClass(p.status)}`}
                    title={p.status}
                  >
                    {formatStatus(p.status)}
                  </div>
                  {canCancelDirectDebit(p) && (
                    <button
                      type="button"
                      className="orenda-benes-row-delete"
                      onClick={() => {
                        setCancelError(null);
                        setCancelTarget(p);
                      }}
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {tab === "scheduled" && schedPhase === "loading" && (
        <div className="orenda-benes-card orenda-benes-empty">Loading…</div>
      )}

      {tab === "scheduled" && schedPhase === "error" && (
        <div className="orenda-benes-card orenda-benes-empty">
          <p>{schedError}</p>
          <button
            type="button"
            className="orenda-btn-primary"
            onClick={loadScheduled}
          >
            Try again
          </button>
        </div>
      )}

      {tab === "scheduled" && schedPhase === "ready" && schedList.length === 0 && (
        <div className="orenda-benes-card orenda-benes-empty">
          <h3>No scheduled payments</h3>
          <p>
            Set a future date on a payment and it'll wait here until it
            runs. One-off or on a repeating schedule.
          </p>
        </div>
      )}

      {tab === "scheduled" && schedPhase === "ready" && schedList.length > 0 && (
        <ul className="orenda-benes-list orenda-pay-list">
          {schedList.map((sp) => {
            const payeeName = sp?.payee?.name || "—";
            const freq = formatFrequency(sp.frequency);
            return (
              <li
                key={sp.id}
                className="orenda-benes-row orenda-pay-row orenda-pay-row-scheduled"
              >
                <div className="orenda-benes-row-main">
                  <div className="orenda-benes-row-name">
                    {payeeName}
                    {freq && (
                      <span className="orenda-pay-chip orenda-pay-chip--neutral orenda-pay-chip--dd">
                        {freq}
                      </span>
                    )}
                  </div>
                  <div className="orenda-benes-row-account orenda-pay-row-sub">
                    <span>Scheduled for {formatScheduledFor(sp)}</span>
                    {formatScheduledPayeeAccount(sp) && (
                      <span className="orenda-pay-ref">
                        {formatScheduledPayeeAccount(sp)}
                      </span>
                    )}
                    {sp.purpose && (
                      <span className="orenda-pay-ref">{sp.purpose}</span>
                    )}
                  </div>
                </div>
                <div className="orenda-pay-row-right">
                  <div className="orenda-pay-amount">
                    {formatAmount(sp.amount, sp.currency)}
                  </div>
                  <button
                    type="button"
                    className="orenda-benes-row-delete"
                    onClick={() => {
                      setCancelScheduledError(null);
                      setCancelScheduled(sp);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {sendOpen && (
        <OrendaSendMoney
          onClose={() => setSendOpen(false)}
          onSent={async () => {
            setSendOpen(false);
            // A payment can be either immediate (lands in `recent`) or
            // scheduled (lands in `scheduled`). Refresh both so whichever
            // tab the user flips to shows the new row.
            await Promise.all([loadList(), loadScheduled()]);
          }}
        />
      )}

      {cancelScheduled && (
        <ConfirmCancelScheduled
          target={cancelScheduled}
          busy={cancelScheduledBusy}
          error={cancelScheduledError}
          onCancel={() => {
            if (cancelScheduledBusy) return;
            setCancelScheduled(null);
            setCancelScheduledError(null);
          }}
          onConfirm={runDeleteScheduled}
        />
      )}

      {cancelTarget && (
        <ConfirmCancelDirectDebit
          target={cancelTarget}
          busy={cancelBusy}
          error={cancelError}
          onCancel={() => {
            if (cancelBusy) return;
            setCancelTarget(null);
            setCancelError(null);
          }}
          onConfirm={runCancelDirectDebit}
        />
      )}
    </section>
  );
};

const ConfirmCancelScheduled = ({ target, busy, error, onCancel, onConfirm }) => (
  <div
    className="orenda-reveal-backdrop"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="orenda-sched-cancel-title"
    onClick={(e) => {
      if (e.target === e.currentTarget && !busy) onCancel?.();
    }}
  >
    <div className="orenda-reveal" style={{ maxWidth: 440 }}>
      <div className="orenda-reveal-head">
        <div>
          <div className="orenda-reveal-eyebrow">Cancel scheduled payment</div>
          <h2 id="orenda-sched-cancel-title" className="orenda-reveal-title">
            Cancel this payment?
          </h2>
        </div>
      </div>
      <div className="orenda-reveal-body">
        <p className="orenda-reveal-expired-sub">
          We'll cancel this scheduled payment and stop any future runs on
          the same schedule. You can create a new one later if you change
          your mind.
        </p>
        <div className="orenda-pay-cancel-meta">
          <span>{target?.payee?.name || "—"}</span>
          <span>{formatAmount(target?.amount, target?.currency)}</span>
        </div>
        <div className="orenda-pay-cancel-meta">
          <span>Scheduled for</span>
          <span>{formatScheduledFor(target)}</span>
        </div>
        {error && (
          <div
            className="orenda-reveal-countdown"
            style={{ color: "#c04040" }}
          >
            {error}
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="orenda-btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Keep it
          </button>
          <button
            type="button"
            className="orenda-btn-danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Cancelling…" : "Yes, cancel"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

const ConfirmCancelDirectDebit = ({ target, busy, error, onCancel, onConfirm }) => (
  <div
    className="orenda-reveal-backdrop"
    role="alertdialog"
    aria-modal="true"
    aria-labelledby="orenda-pay-cancel-title"
    onClick={(e) => {
      if (e.target === e.currentTarget && !busy) onCancel?.();
    }}
  >
    <div className="orenda-reveal" style={{ maxWidth: 440 }}>
      <div className="orenda-reveal-head">
        <div>
          <div className="orenda-reveal-eyebrow">Cancel direct debit</div>
          <h2
            id="orenda-pay-cancel-title"
            className="orenda-reveal-title"
          >
            Cancel this collection?
          </h2>
        </div>
      </div>
      <div className="orenda-reveal-body">
        <p className="orenda-reveal-expired-sub">
          We'll ask the bank to refuse this direct debit. It's accepted
          immediately but the final result (FAILED) can take a short while
          to confirm. Cutoff is 09:00 Europe/Amsterdam on collection day.
        </p>
        <div className="orenda-pay-cancel-meta">
          <span>{target?.payee?.name || "—"}</span>
          <span>{formatAmount(target?.amount, target?.currency)}</span>
        </div>
        {error && (
          <div
            className="orenda-reveal-countdown"
            style={{ color: "#c04040" }}
          >
            {error}
          </div>
        )}
        <div
          style={{
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            marginTop: 16,
            flexWrap: "wrap",
          }}
        >
          <button
            type="button"
            className="orenda-btn-ghost"
            onClick={onCancel}
            disabled={busy}
          >
            Keep it
          </button>
          <button
            type="button"
            className="orenda-btn-danger"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Cancelling…" : "Yes, cancel"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

const SendIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <line x1="22" y1="2" x2="11" y2="13" />
    <polygon points="22 2 15 22 11 13 2 9 22 2" />
  </svg>
);

export default OrendaPayments;
